import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShieldCheckIcon, 
  EyeIcon, 
  EyeSlashIcon, 
  UserIcon, 
  EnvelopeIcon, 
  LockClosedIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  TvIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import Header from './common/Header';
import Footer from './common/Footer';
import UserTypeToggle from './common/UserTypeToggle';

const LoginRegistrationPage = () => {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [userType, setUserType] = useState('screen-owner');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    businessName: '',
    companyName: '',
    phone: '',
    location: '',
    screenType: '',
    screenSize: '',
    campaignObjective: '',
    budgetRange: '',
    website: ''
  });

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (mode === 'register') {
      if (!formData.firstName) {
        newErrors.firstName = 'First name is required';
      }
      
      if (!formData.lastName) {
        newErrors.lastName = 'Last name is required';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

      if (userType === 'screen-owner') {
        if (!formData.businessName) {
          newErrors.businessName = 'Business name is required';
        }
        if (!formData.location) {
          newErrors.location = 'Location is required';
        }
        if (!formData.screenType) {
          newErrors.screenType = 'Screen type is required';
        }
      } else {
        if (!formData.companyName) {
          newErrors.companyName = 'Company name is required';
        }
        if (!formData.campaignObjective) {
          newErrors.campaignObjective = 'Campaign objective is required';
        }
        if (!formData.budgetRange) {
          newErrors.budgetRange = 'Budget range is required';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSuccess('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (mode === 'login') {
        setSuccess('Login successful! Redirecting to dashboard...');
        setTimeout(() => {
          navigate(userType === 'screen-owner' ? '/dashboard' : '/advertiser-dashboard');
        }, 1500);
      } else {
        setSuccess('Registration successful! Please check your email for verification.');
        setTimeout(() => {
          setMode('login');
          setSuccess('');
        }, 2000);
      }
    } catch (error) {
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    setLoading(true);
    // Simulate social login
    setTimeout(() => {
      setSuccess(`${provider} login successful! Redirecting...`);
      setTimeout(() => {
        navigate(userType === 'screen-owner' ? '/dashboard' : '/advertiser-dashboard');
      }, 1500);
    }, 1000);
  };

  const getPasswordStrength = (password) => {
    if (password.length < 8) return { strength: 'weak', color: 'text-error' };
    if (password.length < 12) return { strength: 'medium', color: 'text-warning' };
    return { strength: 'strong', color: 'text-success' };
  };

  return (
    <div className="min-h-screen bg-surface">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Mode Toggle */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-6">
            {/*<ShieldCheckIcon className="w-12 h-12 text-primary mr-4" />*/}
            <h1 className="text-3xl font-bold text-text-primary">
              {mode === 'login' ? 'Welcome Back' : 'Join Sy9 Network'}
            </h1>
          </div>
          
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-full p-1 shadow-card">
              <button 
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                  mode === 'login' 
                    ? 'bg-primary text-white' 
                    : 'text-text-secondary hover:text-primary'
                }`}
                onClick={() => setMode('login')}
              >
                Login
              </button>
              <button 
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                  mode === 'register' 
                    ? 'bg-primary text-white' 
                    : 'text-text-secondary hover:text-primary'
                }`}
                onClick={() => setMode('register')}
              >
                Register
              </button>
            </div>
          </div>
        </div>

        {/* User Type Toggle - Only show in register mode */}
        {mode === 'register' && (
          <UserTypeToggle 
            userType={userType} 
            onUserTypeChange={setUserType} 
          />
        )}

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center">
            <CheckCircleIcon className="w-5 h-5 text-success mr-3" />
            <span className="text-success">{success}</span>
          </div>
        )}

        {errors.general && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-center">
            <ExclamationCircleIcon className="w-5 h-5 text-error mr-3" />
            <span className="text-error">{errors.general}</span>
          </div>
        )}

        {/* Main Form */}
        <div className="bg-white rounded-lg shadow-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mode === 'register' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      First Name *
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-3 h-5 w-5 text-text-secondary" />
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                          errors.firstName ? 'border-error' : 'border-border'
                        }`}
                        placeholder="Enter your first name"
                      />
                    </div>
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-error">{errors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Last Name *
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-3 h-5 w-5 text-text-secondary" />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                          errors.lastName ? 'border-error' : 'border-border'
                        }`}
                        placeholder="Enter your last name"
                      />
                    </div>
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-error">{errors.lastName}</p>
                    )}
                  </div>
                </>
              )}

              <div className={mode === 'login' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Email Address *
                </label>
               <div className="flex items-center border rounded-lg">
                  <EnvelopeIcon className="h-5 w-5 text-text-secondary mx-3" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full pr-4 py-3 focus:outline-none ${
                      errors.email ? 'border-error' : ''}`}
                      placeholder="Enter your email address"
                    />
                    </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-error">{errors.email}</p>
                )}
              </div>

              <div className={mode === 'login' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Password *
                </label>
                <div className="flex items-center border">
                  <LockClosedIcon className="h-5 w-5 text-text-secondary mx-3" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`flex-1 py-3 focus:outline-none ${
                    errors.password ? 'border-error' : ''}`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="mr-3 h-5 w-5 text-text-secondary hover:text-primary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                     {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                   </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-error">{errors.password}</p>
                )}
                {mode === 'register' && formData.password && (
                  <p className={`mt-1 text-sm ${getPasswordStrength(formData.password).color}`}>
                    Password strength: {getPasswordStrength(formData.password).strength}
                  </p>
                )}
              </div>

              {mode === 'register' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-3 h-5 w-5 text-text-secondary" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                        errors.confirmPassword ? 'border-error' : 'border-border'
                      }`}
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-3 h-5 w-5 text-text-secondary hover:text-primary"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-error">{errors.confirmPassword}</p>
                  )}
                </div>
              )}
            </div>

            {/* Progressive Disclosure - User Type Specific Fields */}
            {mode === 'register' && (
              <div className="mt-8 pt-6 border-t border-border">
                <h3 className="text-lg font-semibold text-text-primary mb-4">
                  {userType === 'screen-owner' ? 'Business Information' : 'Company Information'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {userType === 'screen-owner' ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Business Name *
                        </label>
                        <div className="relative">
                          <BuildingOfficeIcon className="absolute left-3 top-3 h-5 w-5 text-text-secondary" />
                          <input
                            type="text"
                            name="businessName"
                            value={formData.businessName}
                            onChange={handleInputChange}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                              errors.businessName ? 'border-error' : 'border-border'
                            }`}
                            placeholder="Enter your business name"
                          />
                        </div>
                        {errors.businessName && (
                          <p className="mt-1 text-sm text-error">{errors.businessName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Location *
                        </label>
                        <div className="relative">
                          <MapPinIcon className="absolute left-3 top-3 h-5 w-5 text-text-secondary" />
                          <input
                            type="text"
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                              errors.location ? 'border-error' : 'border-border'
                            }`}
                            placeholder="Enter your location"
                          />
                        </div>
                        {errors.location && (
                          <p className="mt-1 text-sm text-error">{errors.location}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Screen Type *
                        </label>
                        <div className="relative">
                          <TvIcon className="absolute left-3 top-3 h-5 w-5 text-text-secondary" />
                          <select
                            name="screenType"
                            value={formData.screenType}
                            onChange={handleInputChange}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                              errors.screenType ? 'border-error' : 'border-border'
                            }`}
                          >
                            <option value="">Select screen type</option>
                            <option value="led">LED Display</option>
                            <option value="lcd">LCD Display</option>
                            <option value="projection">Projection Screen</option>
                            <option value="digital-billboard">Digital Billboard</option>
                          </select>
                        </div>
                        {errors.screenType && (
                          <p className="mt-1 text-sm text-error">{errors.screenType}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Screen Size
                        </label>
                        <input
                          type="text"
                          name="screenSize"
                          value={formData.screenSize}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                          placeholder="e.g., 55 inches, 2x3 meters"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Company Name *
                        </label>
                        <div className="relative">
                          <BuildingOfficeIcon className="absolute left-3 top-3 h-5 w-5 text-text-secondary" />
                          <input
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                              errors.companyName ? 'border-error' : 'border-border'
                            }`}
                            placeholder="Enter your company name"
                          />
                        </div>
                        {errors.companyName && (
                          <p className="mt-1 text-sm text-error">{errors.companyName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Website
                        </label>
                        <input
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                          placeholder="https://your-website.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Campaign Objective *
                        </label>
                        <select
                          name="campaignObjective"
                          value={formData.campaignObjective}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                            errors.campaignObjective ? 'border-error' : 'border-border'
                          }`}
                        >
                          <option value="">Select campaign objective</option>
                          <option value="brand-awareness">Brand Awareness</option>
                          <option value="lead-generation">Lead Generation</option>
                          <option value="sales">Sales</option>
                          <option value="traffic">Website Traffic</option>
                          <option value="engagement">Engagement</option>
                        </select>
                        {errors.campaignObjective && (
                          <p className="mt-1 text-sm text-error">{errors.campaignObjective}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Budget Range *
                        </label>
                        <div className="relative">
                          <CurrencyDollarIcon className="absolute left-3 top-3 h-5 w-5 text-text-secondary" />
                          <select
                            name="budgetRange"
                            value={formData.budgetRange}
                            onChange={handleInputChange}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                              errors.budgetRange ? 'border-error' : 'border-border'
                            }`}
                          >
                            <option value="">Select budget range</option>
                            <option value="under-1000">Under $1,000</option>
                            <option value="1000-5000">$1,000 - $5,000</option>
                            <option value="5000-10000">$5,000 - $10,000</option>
                            <option value="10000-25000">$10,000 - $25,000</option>
                            <option value="over-25000">Over $25,000</option>
                          </select>
                        </div>
                        {errors.budgetRange && (
                          <p className="mt-1 text-sm text-error">{errors.budgetRange}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Remember Me & Forgot Password */}
            {mode === 'login' && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                  />
                  <label htmlFor="rememberMe" className="ml-2 text-sm text-text-secondary">
                    Remember me
                  </label>
                </div>
                <button
                  type="button"
                  className="text-sm text-primary hover:text-primary-dark transition-colors"
                  onClick={() => alert('Password recovery feature coming soon!')}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                  {mode === 'login' ? 'Signing In...' : 'Creating Account...'}
                </div>
              ) : (
                mode === 'login' ? 'Sign In' : 'Create Account'
              )}
            </button>

            {/* Social Login */}
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-text-secondary">
                    Or continue with
                  </span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleSocialLogin('Google')}
                  className="w-full flex items-center justify-center py-3 px-4 border border-border rounded-lg shadow-sm bg-white text-sm font-medium text-text-secondary hover:bg-surface transition-colors">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google</span>
</button>
                
                <button
                  type="button"
                  onClick={() => handleSocialLogin('LinkedIn')}
                   className="w-full flex items-center justify-center py-3 px-4 border border-border rounded-lg shadow-sm bg-white text-sm font-medium text-text-secondary hover:bg-surface transition-colors">
                 <svg className="w-5 h-5 mr-2" fill="#0077B5" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>LinkedIn
                </button>
              </div>
            </div>

            {/* Terms and Privacy */}
            {mode === 'register' && (
              <div className="text-center text-sm text-text-secondary">
                By creating an account, you agree to our{' '}
                <button type="button" className="text-primary hover:text-primary-dark">
                  Terms of Service
                </button>{' '}
                and{' '}
                <button type="button" className="text-primary hover:text-primary-dark">
                  Privacy Policy
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 text-center">
          <div className="flex justify-center items-center space-x-6 text-text-secondary">
            <div className="flex items-center">
              <ShieldCheckIcon className="w-5 h-5 mr-2" />
              <span className="text-sm">SSL Secured</span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="w-5 h-5 mr-2" />
              <span className="text-sm">Privacy Protected</span>
            </div>
            <div className="flex items-center">
              <LockClosedIcon className="w-5 h-5 mr-2" />
              <span className="text-sm">Data Encrypted</span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LoginRegistrationPage;